import {
  FlashcardDecksService,
  type QuestionPublic,
  QuestionsService,
} from "@/client"
import { PageShell } from "@/components/Layout"
import { QuizQuestionBank } from "@/components/Quizzes/QuizQuestionBank"
import { Field } from "@/components/ui/field"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import { Button, Text, Textarea, VStack } from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { useForm } from "react-hook-form"

export const Route = createFileRoute("/_layout/flashcards/$deckId/cards/add")({
  component: AddCardPage,
})

interface FormValues {
  front: string
  back: string
  source_question_id?: string | null
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")

const buildBackFromQuestion = (question: QuestionPublic) => {
  const answer = question.options[question.correct_answer] ?? ""
  const rationale = question.rationale?.trim()
  return [
    `<p><strong>Answer:</strong> ${escapeHtml(answer)}</p>`,
    rationale ? `<div>${rationale}</div>` : "",
  ]
    .filter(Boolean)
    .join("")
}

function AddCardPage() {
  const { deckId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const breadcrumbs = useRouteBreadcrumbs("flashcards.index", [
    { label: "Edit Deck", href: `/flashcards/${deckId}/edit` },
    { label: "Add Card" },
  ])
  const [searchValue, setSearchValue] = useState("")
  const [selectedSubjects, setSelectedSubjects] = useState<
    Array<{ id: string; name: string }>
  >([])
  const [selectedTopics, setSelectedTopics] = useState<
    Array<{ id: string; name: string }>
  >([])
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>()

  const selectedQuestionId = watch("source_question_id")

  const { data: questionsData, isLoading: questionsLoading } = useQuery({
    queryKey: [
      "flashcard-question-bank",
      {
        search: searchValue,
        subjectIds: selectedSubjects.map((subject) => subject.id),
        topicIds: selectedTopics.map((topic) => topic.id),
      },
    ],
    queryFn: () =>
      QuestionsService.listQuestions({
        search: searchValue.trim() || undefined,
        subjectIds:
          selectedSubjects.length > 0
            ? selectedSubjects.map((subject) => subject.id)
            : undefined,
        topicIds:
          selectedTopics.length > 0
            ? selectedTopics.map((topic) => topic.id)
            : undefined,
        limit: 20,
      }),
  })

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      FlashcardDecksService.addCard({ deckId, requestBody: values }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["flashcard-deck-cards", deckId],
      })
      navigate({ to: `/flashcards/${deckId}/edit` })
    },
  })

  const handleAddQuestion = (question: QuestionPublic) => {
    setValue("front", question.question_text, { shouldDirty: true })
    setValue("back", buildBackFromQuestion(question), { shouldDirty: true })
    setValue("source_question_id", question.id, { shouldDirty: true })
  }

  return (
    <PageShell title="Add Card" breadcrumbs={breadcrumbs}>
      <QuizQuestionBank
        loading={questionsLoading}
        availableQuestions={questionsData?.data ?? []}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        selectedSubjects={selectedSubjects}
        onSubjectsChange={setSelectedSubjects}
        selectedTopics={selectedTopics}
        onTopicsChange={setSelectedTopics}
        isQuestionSelected={(questionId) => questionId === selectedQuestionId}
        onAddQuestion={handleAddQuestion}
        onPreviewQuestion={() => undefined}
      />
      <form onSubmit={handleSubmit((v) => mutation.mutateAsync(v))}>
        <VStack gap={4} align="stretch">
          <Field
            label="Front (question / prompt)"
            invalid={!!errors.front}
            errorText={errors.front?.message}
          >
            <Textarea
              {...register("front", { required: "Front is required" })}
              aria-label="Front of card"
              rows={3}
            />
          </Field>
          <Field
            label="Back (answer / rationale)"
            invalid={!!errors.back}
            errorText={errors.back?.message}
          >
            <Textarea
              {...register("back", { required: "Back is required" })}
              aria-label="Back of card"
              rows={5}
            />
          </Field>
          {mutation.isError && (
            <Text color="red.500" fontSize="sm">
              Failed to save card.
            </Text>
          )}
          <Button type="submit" disabled={isSubmitting || mutation.isPending}>
            Save Card
          </Button>
        </VStack>
      </form>
    </PageShell>
  )
}
